import { somarDias } from '@/lib/dia-da-operacao';
import { comCorpo, pedir } from '@/lib/pedido';
import { numeroDoCampo, type AcertoDaApi, type Instrumento } from '@/modulos/combustivel/folhas';

/**
 * A folha de turno (0041; módulo 03; decisões 9, 10 e 12): a porta para o
 * backend e a parte que não depende de tela. O mesmo desenho da folha de
 * combustível — instrumento, taxa de acerto e leitura do número escrito vêm
 * de lá, e não são copiados.
 */

export { ROTULO_DO_INSTRUMENTO, mesCorrente, type Instrumento } from '@/modulos/combustivel/folhas';

export const CAMPOS_DO_TURNO = [
  'data',
  'turno',
  'horimetroInicial',
  'horimetroFinal',
  'producao',
  'espera',
  'deslocamento',
  'manutencao',
  'operador',
  /* A causa da parada (0086): o código do catálogo, na linha que teve espera
     ou manutenção. Ela não entra na taxa de acerto (ver o servidor). */
  'causa',
] as const;
export type CampoDoTurno = (typeof CAMPOS_DO_TURNO)[number];

export const ROTULO_DO_CAMPO_DO_TURNO: Readonly<Record<CampoDoTurno, string>> = {
  data: 'data',
  turno: 'turno',
  horimetroInicial: 'horímetro inicial',
  horimetroFinal: 'horímetro final',
  producao: 'produção',
  espera: 'espera',
  deslocamento: 'deslocamento',
  manutencao: 'manutenção',
  operador: 'operador',
  causa: 'causa da parada',
};

/** Uma causa do catálogo (0014), como a folha a imprime e a conferência a oferece. */
export interface CausaDaFolha {
  readonly id: string;
  readonly codigo: string;
  readonly nome: string;
}

export interface FolhaDeTurnoDaApi {
  readonly id: string;
  readonly codigo: string;
  readonly de: string;
  readonly ate: string;
  readonly emitidaEm: string;
  readonly equipamentoId: string;
  readonly equipamentoCodigo: string;
  readonly equipamentoDescricao: string;
  readonly empresa: string;
  readonly emitidaPor: string | null;
  readonly leituras: number;
  readonly conferidas: number;
  readonly lancados: number;
}

export interface TurnoLidoDaApi {
  readonly linha: number;
  readonly data: string | null;
  readonly turno: string | null;
  readonly turnoEscrito: string | null;
  readonly horimetroInicial: number | null;
  readonly horimetroFinal: number | null;
  readonly producao: number | null;
  readonly espera: number | null;
  readonly deslocamento: number | null;
  readonly manutencao: number | null;
  readonly operador: string | null;
  readonly operadorId: string | null;
  /** A causa escrita e a do catálogo que ela casou (0086). A leitura de antes não as tem. */
  readonly causaEscrita?: string | null;
  readonly causaId?: string | null;
  readonly duvidas: readonly CampoDoTurno[];
}

export interface LeituraDeTurnoDaApi {
  readonly id: string;
  readonly folhaId: string;
  readonly mime: string;
  readonly instrumento: Instrumento;
  readonly linhasLidas: readonly TurnoLidoDaApi[];
  readonly lidaEm: string;
  readonly lidaPor: string | null;
  readonly conferidaEm: string | null;
  readonly conferidaPor: string | null;
  readonly camposLidos: number | null;
  readonly camposCorrigidos: number | null;
  readonly apontamentosLancados: number | null;
}

export interface TurnoEnviado {
  readonly origem: number | null;
  readonly incluir: boolean;
  readonly data: string;
  readonly turno: string;
  readonly horimetroInicial: number;
  readonly horimetroFinal: number;
  readonly producao: number;
  readonly espera: number;
  readonly deslocamento: number;
  readonly manutencao: number;
  readonly operadorId: string | null;
  readonly causaId: string | null;
}

export const apiDaFolhaDeTurno = {
  folhas: () =>
    pedir<{ folhas: readonly FolhaDeTurnoDaApi[]; acerto: readonly AcertoDaApi[]; leituraLigada: boolean }>('/operacao/folhas'),
  folha: (id: string) =>
    pedir<{
      folha: FolhaDeTurnoDaApi;
      leituras: readonly LeituraDeTurnoDaApi[];
      pessoas: readonly { id: string; nome: string }[];
      turnos: readonly string[];
      /** O catálogo de causas (0086). O servidor de antes não o manda. */
      causas?: readonly CausaDaFolha[];
    }>(`/operacao/folhas/${encodeURIComponent(id)}`),
  emitir: (envio: { equipamentoId: string; de: string; ate: string }) =>
    comCorpo<FolhaDeTurnoDaApi>('POST', '/operacao/folhas', envio),
  /** A imagem vai crua: o servidor lê o tipo pelo conteúdo. */
  escanear: (folhaId: string, instrumento: Instrumento, arquivo: File) =>
    pedir<{ leitura: LeituraDeTurnoDaApi; codigoLido: string | null; codigoConfere: boolean | null }>(
      `/operacao/folhas/${encodeURIComponent(folhaId)}/leituras?instrumento=${instrumento}`,
      { method: 'POST', body: arquivo, headers: { 'content-type': arquivo.type || 'application/octet-stream' } },
    ),
  imagem: (leituraId: string) =>
    pedir<{ url: string; mime: string }>(`/operacao/folhas/leituras/${encodeURIComponent(leituraId)}/arquivo`),
  conferir: (leituraId: string, linhas: readonly TurnoEnviado[]) =>
    comCorpo<{ lancados: number; camposLidos: number; camposCorrigidos: number }>(
      'POST',
      `/operacao/folhas/leituras/${encodeURIComponent(leituraId)}/conferencia`,
      { linhas },
    ),
};

/** Uma linha na tela da conferência: tudo texto, como o campo de formulário guarda. */
export interface TurnoEmConferencia {
  readonly chave: string;
  readonly origem: number | null;
  readonly incluir: boolean;
  readonly data: string;
  readonly turno: string;
  readonly horimetroInicial: string;
  readonly horimetroFinal: string;
  readonly producao: string;
  readonly espera: string;
  readonly deslocamento: string;
  readonly manutencao: string;
  readonly operadorId: string;
  /** A causa da parada (0086): o id do catálogo, ou vazio. */
  readonly causaId: string;
  readonly duvidas: readonly CampoDoTurno[];
  readonly turnoEscrito: string | null;
  readonly operadorEscrito: string | null;
  readonly causaEscrita: string | null;
}

const paraCampo = (n: number | null) => (n === null ? '' : String(n).replace('.', ','));

/** A conferência abre com o que a IA leu, e marca como dúvida o campo que ela não conseguiu ler. */
export function turnosParaConferir(lidas: readonly TurnoLidoDaApi[]): TurnoEmConferencia[] {
  return lidas.map((l) => {
    const duvidas = new Set<CampoDoTurno>(l.duvidas);
    if (l.data === null) duvidas.add('data');
    if (l.turno === null) duvidas.add('turno');
    if (l.horimetroInicial === null) duvidas.add('horimetroInicial');
    if (l.horimetroFinal === null) duvidas.add('horimetroFinal');
    if (l.operador !== null && l.operadorId === null) duvidas.add('operador');
    if ((l.causaEscrita ?? null) !== null && (l.causaId ?? null) === null) duvidas.add('causa');
    return {
      chave: `lida-${String(l.linha)}`,
      origem: l.linha,
      incluir: true,
      data: l.data ?? '',
      turno: l.turno ?? '',
      horimetroInicial: paraCampo(l.horimetroInicial),
      horimetroFinal: paraCampo(l.horimetroFinal),
      producao: paraCampo(l.producao),
      espera: paraCampo(l.espera),
      deslocamento: paraCampo(l.deslocamento),
      manutencao: paraCampo(l.manutencao),
      operadorId: l.operadorId ?? '',
      causaId: l.causaId ?? '',
      duvidas: [...duvidas],
      turnoEscrito: l.turnoEscrito,
      operadorEscrito: l.operador,
      causaEscrita: l.causaEscrita ?? null,
    };
  });
}

export function turnoNovo(n: number): TurnoEmConferencia {
  return {
    chave: `nova-${String(n)}`, origem: null, incluir: true, data: '', turno: '', horimetroInicial: '', horimetroFinal: '',
    producao: '', espera: '', deslocamento: '', manutencao: '', operadorId: '', causaId: '', duvidas: [], turnoEscrito: null,
    operadorEscrito: null, causaEscrita: null,
  };
}

/**
 * A LINHA ACRESCENTADA À MÃO JÁ VEM COM O QUE A ANTERIOR DIZ (pedido do dono,
 * 31/10/2026: "facilitar e agilizar ao máximo"): o horímetro final da linha de
 * cima é o inicial desta, o turno e o operador são os mesmos, e a data é o dia
 * seguinte, sem passar do fim da folha. Horas e causa ficam em branco: são do
 * turno novo, e copiar seria inventar.
 */
export function turnoSeguinte(
  anterior: TurnoEmConferencia | undefined,
  n: number,
  periodo: { readonly de: string; readonly ate: string },
): TurnoEmConferencia {
  const nova = turnoNovo(n);
  if (anterior === undefined) return nova;
  const diaSeguinte = /^\d{4}-\d{2}-\d{2}$/.test(anterior.data) ? somarDias(anterior.data, 1) : '';
  return {
    ...nova,
    data: diaSeguinte === '' ? '' : diaSeguinte > periodo.ate ? anterior.data : diaSeguinte,
    turno: anterior.turno,
    horimetroInicial: anterior.horimetroFinal,
    operadorId: anterior.operadorId,
  };
}

/**
 * A regra de UMA linha incluída, e o campo a que a recusa se refere. É ela que
 * deixa a conferência pôr a frase sob a célula recusada (p.16: o erro fica
 * abaixo do campo, nunca num alerta no topo), em vez de "Linha 3: falta o
 * turno." no alto da tela. As regras são as de sempre: `montarEnvioDoTurno`
 * as usa daqui, e as frases continuam as mesmas.
 */
export function recusaDaLinha(
  l: TurnoEmConferencia,
  periodo: { readonly de: string; readonly ate: string },
): { readonly campos: readonly CampoDoTurno[]; readonly frase: string } | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(l.data)) return { campos: ['data'], frase: 'falta a data.' };
  if (l.data < periodo.de || l.data > periodo.ate) return { campos: ['data'], frase: 'a data está fora do período da folha.' };
  if (l.turno.trim() === '') return { campos: ['turno'], frase: 'falta o turno.' };
  const inicial = numeroDoCampo(l.horimetroInicial);
  const final = numeroDoCampo(l.horimetroFinal);
  if (inicial === null || Number.isNaN(inicial)) return { campos: ['horimetroInicial'], frase: 'falta o horímetro inicial.' };
  if (final === null || Number.isNaN(final)) return { campos: ['horimetroFinal'], frase: 'falta o horímetro final.' };
  if (final < inicial) return { campos: ['horimetroFinal'], frase: 'o horímetro final não pode ser menor que o inicial.' };
  const horas: number[] = [];
  for (const [campo, valor, rotulo] of [
    ['producao', l.producao, 'produção'],
    ['espera', l.espera, 'espera'],
    ['deslocamento', l.deslocamento, 'deslocamento'],
    ['manutencao', l.manutencao, 'manutenção'],
  ] as const) {
    const h = numeroDoCampo(valor);
    if (h !== null && (Number.isNaN(h) || h > 24)) return { campos: [campo], frase: `a hora de ${rotulo} precisa ser um número entre 0 e 24.` };
    horas.push(h ?? 0);
  }
  const [producao = 0, espera = 0, deslocamento = 0, manutencao = 0] = horas;
  if (producao + espera + deslocamento + manutencao > 24) {
    return {
      campos: ['producao', 'espera', 'deslocamento', 'manutencao'],
      frase: 'produção, espera, deslocamento e manutenção somam mais de 24 horas.',
    };
  }
  /* A causa explica hora parada (0086): sem espera nem manutenção, não há o que explicar. */
  if (l.causaId !== '' && espera + manutencao <= 0) {
    return { campos: ['causa'], frase: 'a causa da parada pede hora de espera ou de manutenção na linha.' };
  }
  return null;
}

/**
 * Da tela ao envio, com a primeira recusa escrita em frase. As mesmas regras
 * que o servidor cobra (a validação do formulário de apontamento) — aqui para
 * a recusa chegar antes, com o número da linha. Hora em branco vale zero.
 */
export function montarEnvioDoTurno(
  linhas: readonly TurnoEmConferencia[],
  periodo: { readonly de: string; readonly ate: string },
): { readonly linhas: TurnoEnviado[] } | { readonly recusa: string } {
  const envio: TurnoEnviado[] = [];
  for (const [i, l] of linhas.entries()) {
    const n = `Linha ${l.origem === null ? `acrescentada ${String(i + 1)}` : String(l.origem)}`;
    if (!l.incluir) {
      if (l.origem !== null) {
        envio.push({
          origem: l.origem, incluir: false, data: periodo.de, turno: '-', horimetroInicial: 0, horimetroFinal: 0,
          producao: 0, espera: 0, deslocamento: 0, manutencao: 0, operadorId: null, causaId: null,
        });
      }
      continue;
    }
    const recusa = recusaDaLinha(l, periodo);
    if (recusa !== null) return { recusa: `${n}: ${recusa.frase}` };
    /* Daqui em diante a linha passou pela regra: os horímetros são números, e
       hora em branco vale zero. */
    envio.push({
      origem: l.origem, incluir: true, data: l.data, turno: l.turno.trim(),
      horimetroInicial: numeroDoCampo(l.horimetroInicial) ?? 0, horimetroFinal: numeroDoCampo(l.horimetroFinal) ?? 0,
      producao: numeroDoCampo(l.producao) ?? 0, espera: numeroDoCampo(l.espera) ?? 0,
      deslocamento: numeroDoCampo(l.deslocamento) ?? 0, manutencao: numeroDoCampo(l.manutencao) ?? 0,
      operadorId: l.operadorId || null,
      causaId: l.causaId || null,
    });
  }
  return { linhas: envio };
}
